import React from 'react';
import { useTheme } from 'styled-components';
import { IconSymbolResponsiveSizeType, IconSymbolSizeType } from '../../../../../theme/type/icon-symbol-type';
import { BreakpointSizeType } from '../../../../../theme/type/breakpoints-type';
import { breakpointPrefix, cn } from '../../../../../utils/tailwind';
import { getOpacity } from '../../../../../theme/utils/get-rgb';

const iconSymbolSizeClass: Record<IconSymbolSizeType, string> = {
    xsmall: 'h-[3rem] min-h-[3rem] w-[3rem] min-w-[3rem]',
    vsmall: 'h-[3.2rem] min-h-[3.2rem] w-[3.2rem] min-w-[3.2rem]',
    small: 'h-[3.6rem] min-h-[3.6rem] w-[3.6rem] min-w-[3.6rem]',
    medium: 'h-[4rem] min-h-[4rem] w-[4rem] min-w-[4rem]',
    large: 'h-[4.8rem] min-h-[4.8rem] w-[4.8rem] min-w-[4.8rem]',
    vlarge: 'h-[5.2rem] min-h-[5.2rem] w-[5.2rem] min-w-[5.2rem]',
    xlarge: 'h-[8rem] min-h-[8rem] w-[8rem] min-w-[8rem]',
};

const getResponsiveSizeClasses = (size: IconSymbolResponsiveSizeType) => {
    const entries = Object.entries(size) as [BreakpointSizeType, IconSymbolSizeType][];

    return entries.map(([breakpoint, value], index) => {
        const className = iconSymbolSizeClass[value];

        return index === 0 ? className : `${breakpointPrefix[breakpoint]}${className}`;
    });
};

type SkeletonIconSymbolType = {
    size: IconSymbolSizeType | IconSymbolResponsiveSizeType | undefined;
    radius?: number;
};

const SkeletonIconSymbol: React.FC<SkeletonIconSymbolType> = ({ size, radius = 0.5 }: SkeletonIconSymbolType) => {
    const theme = useTheme();

    let sizeClassName = '';

    if (size && typeof size === 'string') {
        sizeClassName = iconSymbolSizeClass[size];
    } else if (size) {
        sizeClassName = getResponsiveSizeClasses(size).join(' ');
    }

    return (
        <div
            className={cn(
                'relative inline-flex overflow-hidden after:absolute after:inset-0 after:animate-[app-skeleton-wave_1.5s_linear_0.5s_infinite] after:bg-[linear-gradient(90deg,transparent,var(--skeleton-wave-color),transparent)] after:content-[""]',
                sizeClassName,
            )}
            style={
                {
                    borderRadius: `${radius}rem`,
                    backgroundColor: getOpacity(theme.palette.color.default.dark, 0.3),
                    '--skeleton-wave-color': theme.palette.color.default.opacity.high,
                } as React.CSSProperties
            }
        />
    );
};

export default SkeletonIconSymbol;
